import React, {useCallback, useRef, useState} from 'react';
import {Form, message, Skeleton} from 'antd';
import {useTranslation} from 'react-i18next';
import './Registration.scss';
import BasicInfoSection from './subviews/basicInfoSection/BasicInfoSection';
import OthersInfoSection from './subviews/othersInfoSection/OthersInfoSection';
import TermsSection from './subviews/termsSection/TermsSection';
import FooterButtonGroup from 'components/footerButtonGroup/FooterButtonGroup';
import {RegistrationFormData, ValidationResult} from 'types/dto/registration';
import {scrollToFirstErrorField} from 'utils/commonUtils';
import {useLocation, useNavigate} from 'react-router-dom';
import PasswordSection from './subviews/passwordSection/PasswordSection';
import {buildEmailSentPath} from 'utils/pathBuilder';
import {FieldData} from 'rc-field-form/es/interface';
import {
  convertToRegistrationRequest,
  mapBackendErrorToFrontend,
  saveRegistration,
  validateRegistration
} from 'services/registrationService';
import {SessionDTO} from 'types/dto/login';
import dayjs from 'dayjs';
// import { useSession } from 'context/SessionContext';
import {sendVerificationEmail} from 'services/emailService';

const INITIAL_FORM_VALUES: RegistrationFormData = {
  titleCode: '',
  lastName: '',
  firstName: '',
  email: '',
  commPref: false,
  agreeTerms: false,
  qn3: [],
  language: '',
  packingPlasticBag: 'no',
  phone: '',
  address: '',
  districtCode: '',
  district: '',
  birthMonth: '',
  birthYear: '',
  birthDay: '',
  howToKnowUs: '',
  howToKnowUsOthers: '',
  asiaMile: '',
  sessionID: '',
  setAsPickup: 'N',
  lang: '',
  qn2: 'no'
};

const Registration: React.FC = () => {
  const {
    t,
    i18n
  } = useTranslation('registration');
  const {t: tCommon} = useTranslation('common');
  const navigate = useNavigate();
  const [form] = Form.useForm<RegistrationFormData>();
  const [isReadonly, setIsReadonly] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const location = useLocation();
  // const { session, setSession } = useSession();

  const containerRef = useRef<HTMLDivElement>(null);

  // Get phone number from URL query parameter
  const phoneNumber = location.state?.phoneNumber || '';

  // Get session from navigation state (passed from Login)
  const sessionFromState = location.state?.session as SessionDTO | undefined;

  // Loading states for child sections
  const [basicInfoLoading, setBasicInfoLoading] = useState(true);
  const [othersInfoLoading, setOthersInfoLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // Overall loading state
  const isLoading = basicInfoLoading || othersInfoLoading;

  // Handle loading state changes from BasicInfoSection
  const handleBasicInfoLoadingChange = useCallback((loading: boolean) => {
    setBasicInfoLoading(loading);
  }, []);

  // Handle loading state changes from OthersInfoSection
  const handleOthersInfoLoadingChange = useCallback((loading: boolean) => {
    setOthersInfoLoading(loading);
  }, []);

  // Handle error state from child components
  const handleLoadError = useCallback((error: boolean) => {
    setLoadError(error);
  }, []);

  const handleNext = async () => {
    try {
      await form.validateFields();

      const formValues = form.getFieldsValue();
      // console.log('local Form validation passed');
      // console.log('Form values:', formValues);

      const valuesWithoutUndefined = createValuesWithoutUndefined(formValues);
      // console.log('valuesWithoutUndefined :', valuesWithoutUndefined);

      try {
        setIsSubmitting(true);
        const validationResult = await validateRegistration(
          'register',
          phoneNumber,
          i18n.language,
          valuesWithoutUndefined
        );

        const isResultOK = dealWithValidationResult(validationResult);

        if (!isResultOK) {
          return;
        } else {
          setIsReadonly(true);

          // Scroll to top
          if (containerRef.current) {
            containerRef.current.scrollTop = 0;
          }
        }
      } catch {
        message.error('Server validation failed');
      } finally {
        setIsSubmitting(false);
      }
    } catch (error: unknown) {
      console.error('Form validation failed:', error);
      scrollToFirstErrorField(error as { errorFields: unknown[] }, form);
    }
  };

  const createValuesWithoutUndefined = (formValues: RegistrationFormData): RegistrationFormData => {
    let month = formValues.birthMonth || '';
    let year = formValues.birthYear || '';

    if (formValues.birthDay) {
      const bDay = dayjs(formValues.birthDay);
      if (bDay.isValid()) {
        month = bDay.format('MM');
        year = bDay.format('YYYY');
      }
    }

    const commPrefValue = formValues.commPref === true || formValues.commPref === 'yes' ? 'yes' : 'no';

    const valuesWithoutUndefined: RegistrationFormData = {
      ...formValues,
      titleCode: formValues.titleCode,
      lastName: formValues.lastName,
      firstName: formValues.firstName,
      email: formValues.email,
      phone: formValues.phone || '',
      language: formValues.language || 'en',
      lang: formValues.lang || 'zh',

      address: formValues.address || '',
      districtCode: formValues.districtCode || '',
      district: formValues.district || '',

      birthMonth: month,
      birthYear: year,

      asiaMile: formValues.asiaMile || '',
      setAsPickup: formValues.setAsPickup || 'N',

      commPref: commPrefValue,
      agreeTerms: !!formValues.agreeTerms,
      howToKnowUs: formValues.howToKnowUs || '',
      howToKnowUsOthers: formValues.howToKnowUsOthers || '',
      qn2: formValues.qn2 || 'no',
      qn3: formValues.qn3 || [],

      packingPlasticBag: formValues.packingPlasticBag || 'no',
      customerID: formValues.customerID || 0,
      result: formValues.result || 0,
      sessionID: formValues.sessionID || '',
    };

    return valuesWithoutUndefined;
  };

  const dealWithValidationResult = (validationResult: ValidationResult) => {

    // Clear the last errors
    const emptyFieldErrors = Object.keys(form.getFieldsValue()).map(
      (name) => ({
        name: name as keyof RegistrationFormData,
        errors: [],
      })
    );
    form.setFields(emptyFieldErrors);

    if (validationResult.result === 'Fail') {

      // new errors from backend
      const errorFields: FieldData<RegistrationFormData>[] = [];

      // Iterate through all properties of validationResult
      Object.entries(validationResult).forEach(([key, value]) => {
        // Skip 'result' and 'sessionID' and phone fields, and only process non-empty error messages
        if (
          key !== 'result' &&
          key !== 'sessionID' &&
          key !== 'phone' &&
          value
        ) {
          // Map backend field name to frontend field name
          const frontendFieldName = mapBackendErrorToFrontend(key);
          if (frontendFieldName) {
            errorFields.push({
              name: frontendFieldName,
              errors: [value as string],
            });
          }
        }
      });

      // Set form field errors
      if (errorFields.length > 0) {
        // console.log('errorFields:', errorFields);
        form.setFields(errorFields);
        scrollToFirstErrorField({errorFields}, form);
      }

      //show phone error by message
      if (validationResult.phone) {
        message.error(validationResult.phone);
      }

      return false;
    } else {
      return true;
    }
  };

  const handleValuesChange = (changedValues: any) => {
    const fieldNames = Object.keys(changedValues);

    if (fieldNames.length > 0) {
      const fieldsToUpdate = fieldNames.map(name => ({
        name: name as any,
        errors: [],
      }));

      form.setFields(fieldsToUpdate);
    }
  };

  const handleContinue = async () => {
    const formValues = form.getFieldsValue(true);
    const valuesWithoutUndefined = createValuesWithoutUndefined(formValues);
    const requestData = convertToRegistrationRequest(
      valuesWithoutUndefined,
      phoneNumber,
      i18n.language
    );

    try {
      setIsSubmitting(true);
      const currentToken = sessionFromState?._tk || '';
      // console.log('sessionFromState:', sessionFromState);
      await saveRegistration(
        'register',
        {
          ...requestData,
          passphrase: valuesWithoutUndefined.newPassword || ''
        },
        currentToken
      );

      await sendVerificationEmail(valuesWithoutUndefined.email);

      message.success(t('registration.success'));

      // setSession({
      //   ...(sessionFromState || {}),
      //   _ml: valuesWithoutUndefined.email
      // } as SessionDTO);

      const path = buildEmailSentPath();

      navigate(path, {
        state: {
          registrationData: requestData,
          session: sessionFromState,
          fromConfirm: true
        }
      });
    } catch (error) {
      console.error('Validation failed:', error);
      message.error('Registration failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setIsReadonly(false);

    // Scroll to top
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  };

  return (
    <div className="registration-container" ref={containerRef}>
      {/* Registration Form */}
      <div className="registration-content">
        {/* Show error if missing phone number */}
        {!phoneNumber && (
          <div className="missing-phone-number-error">
            {t('missingPhoneNumberError')}
          </div>
        )}

        {/* Show loading skeleton overlay while data is loading */}
        {isLoading && !!phoneNumber && (
          <div>
            <Skeleton active paragraph={{rows: 4}}/>
          </div>
        )}

        {/* Show error alert if loading failed */}
        {loadError && !isLoading && !!phoneNumber && (
          <div className="load-data-error">
            {tCommon('error.loadDataError')}
          </div>
        )}

        {/* Form content - always mounted but hidden when loading */}
        <Form<RegistrationFormData>
          form={form}
          layout="vertical"
          className="registration-form"
          validateTrigger="onSubmit"
          onValuesChange={handleValuesChange}
          requiredMark={!isReadonly}
          initialValues={INITIAL_FORM_VALUES}
          style={{
            display:
              !isLoading && !loadError && !!phoneNumber ? 'flex' : 'none',
          }}
        >
          <PasswordSection isHidden={isReadonly} phoneNumber={phoneNumber}/>

          {/* Basic Information Section */}
          <BasicInfoSection
            isReadonly={isReadonly}
            onLoadingChange={handleBasicInfoLoadingChange}
            onLoadError={handleLoadError}
          />

          {/* Others Information Section */}
          <OthersInfoSection
            isReadonly={isReadonly}
            onLoadingChange={handleOthersInfoLoadingChange}
            onLoadError={handleLoadError}
          />

          {/* Terms and Conditions Section */}
          <TermsSection isReadonly={isReadonly}/>

          <FooterButtonGroup
            onContinue={isReadonly ? handleContinue : handleNext}
            onCancel={isReadonly ? handleBack : undefined}
            continueText={
              isReadonly ? tCommon('button.confirm') : t('nextButton')
            }
            cancelText={isReadonly ? tCommon('button.back') : ''}
            isSubmitting={isSubmitting}
            isContinueDisabled={isReadonly ? isSubmitting : false}
          />
        </Form>
      </div>
    </div>
  );
};

export default Registration;